import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-3102" data-mp="3jf6le6i1hzon">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-3103" data-mp="zvwv3kzhy8g">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-3104" data-mp="3scyzj7c0z1ag">
        {children}
      </div>
    </motion.section>;
}
export function Terms() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-3105" data-mp="2zol2atsrg8zy">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-3106" data-mp="qoso0sc3hduf">
        <div className="max-w-3xl mx-auto" data-id="element-3107" data-mp="vmu2ezyksozk">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-3108" data-mp="vlj2xcc5y3e9">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-3109" data-mp="10fex03pb3645">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-3110" data-mp="co9fzl7mq7s9">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-3111" data-mp="lpg0s6hba4rb">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-3112" data-mp="1x3bct6cx5xeb">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-3113" data-mp="3u4z2sc9jq3xi">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-3114" data-mp="6yzukg71y9sq">
            These Terms of Service ("Terms") govern your access to and use of
            the Structured Nutrition website, products, and services
            ("Services"). By using our Services, you agree to these Terms. If
            you do not agree, please do not use the Services.
          </motion.p>

          {/* DISCLAIMERS — two clearly separated callouts */}
          <motion.div variants={fadeUp} className="mb-6 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-3115" data-mp="3fejj51ws341g">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-3116" data-mp="2mpkwbrhpndpt">
              General Content — Educational Only
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-3117" data-mp="t9re3qlql8t8">
              The general content published on this website, in our books,
              guides, downloads, emails, and social media is for educational and
              informational purposes only. Because we don't know your individual
              medical history, medications, or circumstances, this content is
              not personalized nutrition advice and is not a substitute for
              consulting a licensed healthcare provider. Always speak with your
              prescribing physician before making changes to your medication,
              nutrition, or exercise plan — especially while taking GLP-1
              medications. Never disregard professional medical advice or delay
              seeking it because of something you read here.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mb-12 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-3118" data-mp="g2alixytky1k">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-3119" data-mp="72f62gy8egvq">
              1:1 Coaching — Professional Dietetic Services
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-3120" data-mp="uqyfizt0ozpe">
              One-on-one nutrition coaching with Structured Nutrition is
              provided by a Registered Dietitian Nutritionist (RDN) and consists
              of medical nutrition therapy and individualized nutrition
              counseling within the scope of practice of a credentialed
              dietitian. These services complement, but do not replace, the care
              of your prescribing physician or other healthcare providers. We do
              not diagnose disease, prescribe medication, or adjust prescription
              dosing — those decisions remain with your physician. Clients are
              responsible for keeping their medical team informed about the
              nutrition plan we develop together.
            </p>
          </motion.div>

          <Section title="Our Services" data-id="element-3121" data-mp="3j442qcesgu5e">
            <p data-id="element-3122" data-mp="3o3qgcmyj4ceq">
              Structured Nutrition offers nutrition coaching, digital products
              (e-books, downloadable guides), training programs, and related
              educational content. Service descriptions, availability, and
              pricing may change without notice.
            </p>
          </Section>

          <Section title="Accounts" data-id="element-3123" data-mp="1ypkbiei1wv3x">
            <p data-id="element-3124" data-mp="1yqmwy6wlu5cg">
              To access certain features, you'll need to create an account. You
              agree to:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-3125" data-mp="1vmxlyh4fmwbw">
              <li data-id="element-3126" data-mp="1vpa1knmpwuls">Provide accurate and complete information</li>
              <li data-id="element-3127" data-mp="1qp5v7qfixg9a">Keep your login credentials confidential</li>
              <li data-id="element-3128" data-mp="25l3ngxmbu0g8">
                Be responsible for all activity that happens under your account
              </li>
              <li data-id="element-3129" data-mp="20lqz28ft4lbj">
                Notify us immediately of any unauthorized use of your account
              </li>
            </ul>
            <p data-id="element-3130" data-mp="2ekgy3tm6sv2j">
              We may suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="Purchases and Payment" data-id="element-3131" data-mp="29l4cfc71xu1j">
            <p data-id="element-3132" data-mp="2oati9kq3am85">
              Payments are processed securely by Stripe. By purchasing a product
              or service, you authorize us to charge the payment method you
              provide for the full purchase amount, including any applicable
              taxes.
            </p>
            <p data-id="element-3133" data-mp="2tbqy3qnmn46h">
              All prices are listed in U.S. dollars unless otherwise noted. We
              reserve the right to change pricing at any time.
            </p>
          </Section>

          <Section title="Refunds" data-id="element-3134" data-mp="3gyutflsgyeum">
            <p data-id="element-3135" data-mp="3gxt7gm9x07y4">
              Digital products (e-books, guides, downloads) are non-refundable
              once accessed or downloaded. Coaching services may be eligible for
              a refund within 7 days of purchase if no session has yet been
              delivered — contact us to request one. Refunds for ongoing
              programs are evaluated case-by-case.
            </p>
          </Section>

          <Section title="Intellectual Property" data-id="element-3136" data-mp="2y0s2h1gd27u6">
            <p data-id="element-3137" data-mp="2k8vaqyiltzyb">
              All content on this site — including text, graphics, logos, books,
              guides, and training materials — is owned by Structured Nutrition
              or licensed to us and is protected by copyright and other
              intellectual property laws.
            </p>
            <p data-id="element-3138" data-mp="2f990mxy2oztq">
              You may not copy, distribute, resell, or create derivative works
              from our content without our prior written permission. Purchasing
              a digital product grants you a personal, non-transferable license
              to use it.
            </p>
          </Section>

          <Section title="Acceptable Use" data-id="element-3139" data-mp="2lozkvyonhme1">
            <p data-id="element-3140" data-mp="2gr7vbbbhacd0">By using our Services, you agree NOT to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-3141" data-mp="2gqpq3p6u8nsy">
              <li data-id="element-3142" data-mp="2bso4awi12ads">
                Use the Services for any unlawful, harmful, or fraudulent
                purpose
              </li>
              <li data-id="element-3143" data-mp="26zv5ep98pw9m">
                Share, redistribute, or resell our digital products or coaching
                materials
              </li>
              <li data-id="element-3144" data-mp="1y0zwh5eta82m">
                Attempt to gain unauthorized access to our systems or other
                users' accounts
              </li>
              <li data-id="element-3145" data-mp="1t0byfzs60qyw">Harass, abuse, or threaten our team or other users</li>
              <li data-id="element-3146" data-mp="1o2ac95zg0bj7">
                Use our content to provide nutrition or medical advice to others
                as a substitute for your own credentialed practice
              </li>
            </ul>
          </Section>

          <Section title="Disclaimer of Warranties" data-id="element-3147" data-mp="8ib3bp85my09">
            <p data-id="element-3148" data-mp="diytk8ok7atf">
              Our Services are provided "as is" and "as available," without
              warranties of any kind, either express or implied. We do not
              guarantee any specific health, weight, or fitness outcomes from
              using our Services.
            </p>
          </Section>

          <Section title="Limitation of Liability" data-id="element-3149" data-mp="3lxzd2e9qhb5j">
            <p data-id="element-3150" data-mp="3lwehclbonm63">
              To the fullest extent permitted by law, Structured Nutrition and
              its team are not liable for any indirect, incidental, special,
              consequential, or punitive damages arising from your use of the
              Services — including but not limited to health outcomes, lost
              profits, or data loss.
            </p>
            <p data-id="element-3151" data-mp="328yoir6rr3ep">
              Our total liability for any claim arising from these Terms or your
              use of the Services is limited to the amount you paid us in the 12
              months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="Indemnification" data-id="element-3152" data-mp="1ey2wun0gjkfc">
            <p data-id="element-3153" data-mp="1euxavgn4kynu">
              You agree to defend, indemnify, and hold harmless Structured
              Nutrition from any claims, damages, or expenses (including
              reasonable attorneys' fees) arising out of your violation of these
              Terms or your misuse of the Services.
            </p>
          </Section>

          <Section title="Governing Law" data-id="element-3154" data-mp="w60dh3wflq1e">
            <p data-id="element-3155" data-mp="w4z371n8p7kq">
              These Terms are governed by the laws of the State of [Your State],
              without regard to conflict-of-laws principles. Any dispute will be
              resolved in the state or federal courts located in [Your
              County/State].
            </p>
          </Section>

          <Section title="Changes to These Terms" data-id="element-3156" data-mp="8eux8nmkwvqk">
            <p data-id="element-3157" data-mp="xopkbol8em2">
              We may update these Terms from time to time. The "Last updated"
              date at the top reflects the most recent revision. Continued use
              of the Services after changes constitutes acceptance of the
              updated Terms.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-3158" data-mp="3j202ywo2vd1h">
            <p data-id="element-3159" data-mp="3j54fchbp5f38">
              Questions about these Terms? Reach out through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-3160" data-mp="3e59m474g8df0">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-3161" data-mp="2zgvjnxay2qyz">
            This document is provided as a general template and does not
            constitute legal advice. Bracketed placeholders (such as state and
            venue) should be filled in, and the full document should be reviewed
            with an attorney before being relied upon.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}